/**
 * Destinaé — ajout au panier sans changer de page.
 *
 * Utilise le même mécanisme que le bouton d'achat d'Horizon :
 * 1. ajout via l'API panier de Shopify (/cart/add.js) ;
 * 2. événement « cart:lines-update » pour qu'Horizon mette à jour le panier latéral
 *    et le compteur de l'en-tête ;
 * 3. ouverture du panier latéral.
 * Si quelque chose échoue avant l'ajout, on revient au comportement classique (page panier).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = () => (window.Theme && window.Theme.routes) || {};

async function fetchCart() {
  const items = document.querySelector('cart-items-component');
  if (items && typeof items.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const r = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return r.json();
}

function openDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  return Boolean(drawer);
}

/**
 * Ajoute au panier.
 * @param {HTMLElement} source - élément d'origine (formulaire ou bouton)
 * @param {FormData} formData - doit contenir `id` et `quantity` (et éventuellement des `properties[...]`)
 * @returns {Promise<void>} rejetée avec un message en français en cas d'erreur
 */
export async function addToCart(source, formData) {
  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  let response;
  let data;
  try {
    response = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: formData,
    });
    data = await response.json();
  } catch (error) {
    deferred.reject(error);
    throw new Error("L'ajout au panier n'a pas fonctionné. Merci de réessayer.");
  }

  const cart = await fetchCart().catch(() => null);
  const failed = !response.ok || data.status;
  deferred.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
    detail: { items: cart ? cart.items : [], source: 'destinae', didError: Boolean(failed), itemCount: quantity },
  });

  if (failed) {
    /* Shopify refuse l'ajout s'il dépasse le stock disponible : on affiche son message. */
    throw new Error(data.description || data.message || "L'ajout au panier n'a pas fonctionné. Merci de réessayer.");
  }

  if (!openDrawer()) window.location.href = routes().cart_url || '/cart';
}

/**
 * Message quand la quantité demandée dépasse le stock (aussi utilisé par la fiche produit).
 * @param {number} remaining - bouquets encore disponibles (stock moins ceux déjà dans le panier)
 * @param {number} inCart - bouquets de cette taille déjà dans le panier
 * @returns {string}
 */
export function stockMessage(remaining, inCart = 0) {
  if (remaining <= 0) return 'Il ne reste plus de bouquet disponible : ceux qui restaient sont déjà dans votre panier.';
  const base = remaining === 1 ? "Il ne reste qu'un bouquet" : `Il ne reste que ${remaining} bouquets`;
  if (inCart > 0) return `${base} en plus de ${inCart === 1 ? 'celui' : 'ceux'} déjà dans votre panier.`;
  return `${base}.`;
}

/**
 * Stock suivi (attribut data-d-stock du formulaire) : refuse l'envoi si la quantité dépasse ce qu'il reste,
 * au lieu de laisser Shopify ajouter le maximum. Renvoie le message à afficher, ou '' si l'ajout est possible.
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 */
async function checkStock(form, formData) {
  const stock = parseInt(form.dataset.dStock || '', 10);
  if (Number.isNaN(stock)) return '';
  const quantity = Number(formData.get('quantity')) || 1;
  const id = String(formData.get('id') || '');
  const cart = await fetchCart().catch(() => null);
  const inCart = cart ? cart.items.filter((/** @type {any} */ i) => String(i.variant_id) === id).reduce((n, /** @type {any} */ i) => n + i.quantity, 0) : 0;
  const remaining = Math.max(0, stock - inCart);
  return quantity > remaining ? stockMessage(remaining, inCart) : '';
}

/**
 * Branche un formulaire d'ajout au panier : l'envoi ouvre le panier latéral au lieu de changer de page.
 * @param {HTMLFormElement} form
 */
export function enhanceForm(form) {
  if (!form || form.dataset.dCartReady) return;
  form.dataset.dCartReady = 'true';
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[type="submit"]'));
    const error = form.querySelector('[data-d-cart-error]');
    const label = button ? button.innerHTML : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Ajout en cours…';
    }
    if (error) error.setAttribute('hidden', '');
    try {
      const formData = new FormData(form);
      const tooMany = await checkStock(form, formData);
      if (tooMany) throw new Error(tooMany);
      await addToCart(form, formData);
    } catch (e) {
      showError(form, error, /** @type {Error} */ (e).message);
    } finally {
      if (button) {
        button.disabled = false;
        button.innerHTML = label;
      }
    }
  });
}

/**
 * Affiche l'erreur dans la page (jamais de fenêtre d'alerte du navigateur).
 * @param {Element | null} near - élément près duquel afficher le message s'il n'a pas d'emplacement prévu
 * @param {Element | null} slot - emplacement prévu (role="alert")
 * @param {string} message
 */
function showError(near, slot, message) {
  let el = slot;
  if (!el && near) {
    el = near.querySelector('[data-d-error-auto]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('role', 'alert');
      el.setAttribute('data-d-error-auto', '');
      el.className = 'd-upsell__error';
      near.appendChild(el);
    }
  }
  if (!el) return;
  el.textContent = message || "L'ajout n'a pas fonctionné. Réessayez dans un instant.";
  el.removeAttribute('hidden');
}

function enhanceAll(root = document) {
  root.querySelectorAll('form[data-d-cart-form]').forEach((form) => enhanceForm(/** @type {HTMLFormElement} */ (form)));
}

/* Boutons « Ajouter » du panier latéral (offre 2e bouquet) : le panier est re-dessiné après
   chaque changement, on écoute donc les clics au niveau du document. */
document.addEventListener('click', async (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const button = /** @type {HTMLButtonElement | null} */ (target && target.closest ? target.closest('[data-d-cart-add]') : null);
  if (!button || button.disabled) return;
  event.preventDefault();
  const fd = new FormData();
  fd.set('id', button.getAttribute('data-d-cart-add') || '');
  fd.set('quantity', '1');
  /* Montage : option par défaut de la fiche (visible dans le panier) */
  fd.set('properties[Montage]', 'Livré monté, prêt à offrir');
  const label = button.textContent;
  button.disabled = true;
  button.textContent = 'Ajout…';
  try {
    await addToCart(button, fd);
  } catch (e) {
    button.disabled = false;
    button.textContent = label;
    showError(button.closest('.d-upsell') || button.parentElement, button.closest('.d-upsell')?.querySelector('[data-d-upsell-error]') || null, /** @type {Error} */ (e).message);
  }
});

/* Offre Duo avec 1 bouquet : la remise n'est pas encore appliquée au panier, Shopify ne la donne donc pas au thème.
   On la demande à Shopify sur un panier test de 2 bouquets (API Storefront, sans clé), qui n'a aucun lien avec
   le vrai panier du client. Le montant affiché est celui que Shopify calcule réellement.
   Sans remise trouvée (offre arrêtée, erreur), la barre reste cachée. Résultat gardé 10 minutes dans l'onglet. */
const DUO_CACHE_MS = 10 * 60 * 1000;
const duoPending = new Map();

/** @param {string} variantId @returns {Promise<{amount: number, currency: string} | null>} */
function duoAmount(variantId) {
  const key = `d-duo:${variantId}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (cached && Date.now() - cached.t < DUO_CACHE_MS) return Promise.resolve(cached.v);
  } catch (e) {}
  if (duoPending.has(key)) return duoPending.get(key);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const query = `mutation($id: ID!) { cartCreate(input: { lines: [{ merchandiseId: $id, quantity: 2 }] }) {
    cart { discountAllocations { discountedAmount { amount currencyCode } }
      lines(first: 5) { nodes { discountAllocations { discountedAmount { amount currencyCode } } } } } } }`;
  const promise = fetch(`${root}api/2025-07/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${variantId}` } }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((json) => {
      const cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
      if (!cart) return null;
      const all = [...cart.discountAllocations, ...cart.lines.nodes.flatMap((l) => l.discountAllocations)];
      const amount = all.reduce((sum, a) => sum + Number(a.discountedAmount.amount || 0), 0);
      const value = amount > 0 ? { amount, currency: all[0].discountedAmount.currencyCode } : null;
      try {
        sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
      } catch (e) {}
      return value;
    })
    .catch(() => null)
    .finally(() => duoPending.delete(key));
  duoPending.set(key, promise);
  return promise;
}

/** @param {number} amount @param {string} currency */
function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount).replace(/ /g, ' ');
  } catch (e) {
    return `${amount.toFixed(2).replace('.', ',')} €`;
  }
}

function fillDuo() {
  document.querySelectorAll('[data-d-duo-probe]:not([data-d-duo-done])').forEach((box) => {
    box.setAttribute('data-d-duo-done', '');
    const variantId = box.getAttribute('data-d-duo-probe');
    if (!variantId) return;
    duoAmount(variantId).then((value) => {
      if (!value || !box.isConnected) return;
      box.querySelectorAll('[data-d-duo-amount]').forEach((slot) => {
        slot.textContent = formatMoney(value.amount, value.currency);
      });
      box.removeAttribute('hidden');
    });
  });
}

/* Le panier (tiroir et page) est re-dessiné par Horizon à chaque changement : on surveille l'arrivée de nouvelles barres. */
let duoQueued = false;
new MutationObserver(() => {
  if (duoQueued) return;
  duoQueued = true;
  requestAnimationFrame(() => {
    duoQueued = false;
    fillDuo();
  });
}).observe(document.documentElement, { childList: true, subtree: true });
fillDuo();

enhanceAll();
document.addEventListener('shopify:section:load', (e) => enhanceAll(/** @type {any} */ (e.target)));
window.DestinaeCart = { addToCart, enhanceForm, stockMessage };
